
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Building, Plus, ArrowUpDown, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Company } from "@/lib/types";
import CompanyImport from "./CompanyImport";
import { useToast } from "@/hooks/use-toast";
import { useCompanyStore } from "@/lib/store";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

interface CompaniesTableProps {
  companies: Company[];
}

const CompaniesTable = ({ companies }: CompaniesTableProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const { toast } = useToast();
  const updateCompany = useCompanyStore((state) => state.updateCompany);
  const itemsPerPage = 20;

  // Sort companies alphabetically by name
  const sortedCompanies = useMemo(() => {
    return [...companies].sort((a, b) => {
      const comparison = a.name.localeCompare(b.name);
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [companies, sortDirection]);

  // Calculate pagination
  const totalPages = Math.ceil(sortedCompanies.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCompanies = sortedCompanies.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const toggleSort = () => {
    setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const toggleVisibility = (companyId: string, currentVisibility?: boolean) => {
    const newVisibility = !currentVisibility;
    updateCompany(companyId, { isVisible: newVisibility });
    
    toast({
      title: newVisibility ? "Company visible" : "Company hidden",
      description: `This company is now ${newVisibility ? "visible to" : "hidden from"} users`,
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-xl font-medium">Companies</h2>
        <Button
          asChild
          className="bg-redflags-red hover:bg-redflags-red/90 text-white"
        >
          <Link to="/add-company">
            <Plus className="w-4 h-4 mr-2" />
            Add Company
          </Link>
        </Button>
      </div>

      {/* Add the CompanyImport component */}
      <CompanyImport />

      {companies.length === 0 ? (
        <EmptyCompaniesState />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium">
                    <Button 
                      variant="ghost" 
                      onClick={toggleSort}
                      className="hover:bg-transparent p-0 font-medium flex items-center"
                    >
                      Name
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </th>
                  <th className="text-left py-3 px-4 font-medium">Locations</th>
                  <th className="text-left py-3 px-4 font-medium">Date Added</th>
                  <th className="text-center py-3 px-4 font-medium">Visibility</th>
                  <th className="text-right py-3 px-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentCompanies.map((company) => (
                  <tr key={company.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Building className="w-4 h-4 text-redflags-red" />
                        <span>{company.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {company.locations.join(", ")}
                    </td>
                    <td className="py-3 px-4">
                      {new Date(company.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {company.isVisible ? 
                          <Eye className="h-4 w-4 text-green-500" /> : 
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        }
                        <Switch 
                          checked={company.isVisible ?? true}
                          onCheckedChange={() => toggleVisibility(company.id, company.isVisible)}
                        />
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Button
                        asChild
                        variant="ghost"
                        size="sm"
                        className="text-redflags-gray hover:text-redflags-dark"
                      >
                        <Link to={`/edit-company/${company.id}`}>
                          Edit
                        </Link>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination className="mt-4">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious 
                    onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                    className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
                
                {/* Generate page links */}
                {[...Array(totalPages)].map((_, i) => (
                  <PaginationItem key={i + 1}>
                    <PaginationLink
                      isActive={currentPage === i + 1}
                      onClick={() => handlePageChange(i + 1)}
                      className="cursor-pointer"
                    >
                      {i + 1}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                
                <PaginationItem>
                  <PaginationNext 
                    onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                    className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
          
          <div className="text-sm text-gray-500 mt-2">
            Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, companies.length)} of {companies.length} companies
          </div>
        </>
      )}
    </div>
  );
};

const EmptyCompaniesState = () => (
  <div className="text-center py-10 text-redflags-gray">
    <Building className="w-8 h-8 mx-auto mb-3 text-redflags-light" />
    <p>No companies found</p>
    <Button
      asChild
      variant="outline"
      className="mt-4"
    >
      <Link to="/add-company">
        Add your first company
      </Link>
    </Button>
  </div>
);

export default CompaniesTable;
